import React, { useState, useEffect, useMemo } from 'react';
import { attributionAPI } from '../../../services/api';

const formatPct = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number' || !isFinite(v)) return '—';
  return `${v.toFixed(2)}%`;
};

// Weights are stored as decimals (0.14 = 14%) — multiply by 100 to display
const formatWeight = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number' || !isFinite(v)) return '—';
  return `${(v * 100).toFixed(2)}%`;
};

const signClass = (v) => {
  if (v === null || v === undefined || typeof v !== 'number') return '';
  return v >= 0 ? 'pos' : 'neg';
};

// Heat-map shading for Brinson effect cells. Returns an inline style object
// blending toward green (positive) or red (negative) by intensity vs maxAbs.
const heatStyle = (v, maxAbs) => {
  if (v === null || v === undefined || typeof v !== 'number' || !isFinite(v)) return undefined;
  if (!maxAbs || maxAbs === 0) return undefined;
  const intensity = Math.max(-1, Math.min(1, v / maxAbs));
  const alpha = Math.abs(intensity) * 0.55; // cap so text stays readable
  if (intensity > 0) {
    return { backgroundColor: `rgba(145, 207, 155, ${alpha})` }; // Drummond success green
  }
  if (intensity < 0) {
    return { backgroundColor: `rgba(200, 16, 46, ${alpha})` }; // muted red
  }
  return undefined;
};

// Columns that can be sorted, mapped to row → value extractors. Effect columns
// also share a maxAbs scale for the heat-map.
const COLUMNS = [
  { key: 'name', label: 'Holding', numeric: false, align: 'left',
    sacExtract: r => r.sub_asset_class || '', fundExtract: r => r.issue_name || '' },
  { key: 'p_wt', label: 'P Wt', numeric: true,
    sacExtract: r => r.avg_portfolio_weight, fundExtract: r => r.avg_portfolio_weight,
    format: 'weight' },
  { key: 'b_wt', label: 'B Wt', numeric: true,
    sacExtract: r => r.avg_benchmark_weight, fundExtract: r => r.avg_benchmark_weight,
    format: 'weight' },
  { key: 'diff_wt', label: 'Diff Wt', numeric: true,
    sacExtract: r => (r.avg_portfolio_weight ?? 0) - (r.avg_benchmark_weight ?? 0),
    fundExtract: r => r.avg_weight_difference,
    format: 'weight' },
  { key: 'p_ret', label: 'P Ret', numeric: true,
    sacExtract: r => r.portfolio_return, fundExtract: r => r.portfolio_return,
    format: 'pct' },
  { key: 'b_ret', label: 'B Ret', numeric: true,
    sacExtract: r => r.benchmark_return, fundExtract: r => r.benchmark_return,
    format: 'pct' },
  { key: 'diff_ret', label: 'Diff Ret', numeric: true,
    sacExtract: r => r.return_difference, fundExtract: r => r.return_difference,
    format: 'pct' },
  { key: 'alloc', label: 'Alloc', numeric: true, effect: true,
    sacExtract: r => r.allocation_effect, fundExtract: () => null,
    format: 'pct' },
  { key: 'sel', label: 'Sel', numeric: true, effect: true,
    sacExtract: r => r.selection_effect, fundExtract: r => r.selection_effect,
    format: 'pct' },
  { key: 'total', label: 'Total', numeric: true, effect: true,
    sacExtract: r => r.total_effect, fundExtract: r => r.total_effect,
    format: 'pct' },
];

const compareValues = (a, b, dir) => {
  const aNull = a === null || a === undefined || (typeof a === 'number' && !isFinite(a));
  const bNull = b === null || b === undefined || (typeof b === 'number' && !isFinite(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1;  // nulls always last
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') {
    return dir === 'asc' ? a - b : b - a;
  }
  const sa = String(a); const sb = String(b);
  return dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
};

const BrinsonAttributionTable = ({ portfolioCode, reportType, periodEnd, rangeStart, rangeEnd }) => {
  const [sacRows, setSacRows] = useState([]);
  const [fundRows, setFundRows] = useState([]);
  const [portfolioTotal, setPortfolioTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsedSacs, setCollapsedSacs] = useState(new Set());
  const [sort, setSort] = useState({ key: null, dir: 'desc' });

  useEffect(() => {
    if (!portfolioCode) return;

    if (reportType === 'RANGE') {
      if (!rangeStart || !rangeEnd) return;
      setLoading(true);
      const rangeParams = { start_date: rangeStart, end_date: rangeEnd };
      Promise.all([
        attributionAPI.getRangeBrinsonSAC(portfolioCode, rangeParams),
        attributionAPI.getRangeBrinsonFunds(portfolioCode, rangeParams),
        attributionAPI.getRangeBrinsonPortfolioTotal(portfolioCode, rangeParams),
      ])
        .then(([sacs, funds, total]) => {
          setSacRows(sacs);
          setFundRows(funds);
          setPortfolioTotal(total);
          setCollapsedSacs(new Set(sacs.map(s => s.sub_asset_class)));
          setLoading(false);
        })
        .catch((err) => { console.error('Failed to load range Brinson data', err); setLoading(false); });
      return;
    }

    setLoading(true);
    const params = { report_type: reportType };
    if (reportType !== 'SI' && periodEnd) params.period_end = periodEnd;
    Promise.all([
      attributionAPI.getBrinsonSAC(portfolioCode, params),
      attributionAPI.getBrinsonFunds(portfolioCode, params),
      attributionAPI.getBrinsonPortfolioTotal(portfolioCode, params),
    ])
      .then(([sacs, funds, total]) => {
        setSacRows(sacs);
        setFundRows(funds);
        setPortfolioTotal(total);
        setCollapsedSacs(new Set(sacs.map(s => s.sub_asset_class)));
        setLoading(false);
      })
      .catch((err) => { console.error('Failed to load Brinson data', err); setLoading(false); });
  }, [portfolioCode, reportType, periodEnd, rangeStart, rangeEnd]);

  const toggleSac = (sacName) => {
    setCollapsedSacs(prev => {
      const next = new Set(prev);
      if (next.has(sacName)) next.delete(sacName);
      else next.add(sacName);
      return next;
    });
  };

  const allCollapsed = sacRows.length > 0 && collapsedSacs.size === sacRows.length;
  const toggleAll = () => {
    if (allCollapsed) setCollapsedSacs(new Set());
    else setCollapsedSacs(new Set(sacRows.map(s => s.sub_asset_class)));
  };

  // Click cycle: same column → flip direction; new column → start desc
  const onSortClick = (col) => {
    setSort(prev => {
      if (prev.key === col.key) return { key: col.key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      return { key: col.key, dir: col.numeric ? 'desc' : 'asc' };
    });
  };

  // Joint maxAbs across all effect cells (SAC + funds) so the heat-map scales
  // consistently across columns.
  const maxAbsEffect = useMemo(() => {
    let m = 0;
    for (const sac of sacRows) {
      for (const k of ['allocation_effect', 'selection_effect', 'total_effect']) {
        const v = sac[k];
        if (typeof v === 'number' && isFinite(v)) m = Math.max(m, Math.abs(v));
      }
    }
    for (const f of fundRows) {
      for (const k of ['selection_effect', 'total_effect']) {
        const v = f[k];
        if (typeof v === 'number' && isFinite(v)) m = Math.max(m, Math.abs(v));
      }
    }
    return m;
  }, [sacRows, fundRows]);

  // Sort SACs + funds per current sort selection
  const sortedSacRows = useMemo(() => {
    if (!sort.key) return sacRows;
    const col = COLUMNS.find(c => c.key === sort.key);
    if (!col) return sacRows;
    return [...sacRows].sort((a, b) => compareValues(col.sacExtract(a), col.sacExtract(b), sort.dir));
  }, [sacRows, sort]);

  const sortedFundsBySac = useMemo(() => {
    const grouped = {};
    fundRows.forEach(f => {
      if (!grouped[f.sub_asset_class]) grouped[f.sub_asset_class] = [];
      grouped[f.sub_asset_class].push(f);
    });
    if (!sort.key) return grouped;
    const col = COLUMNS.find(c => c.key === sort.key);
    if (!col) return grouped;
    Object.keys(grouped).forEach(sac => {
      grouped[sac] = [...grouped[sac]].sort((a, b) => compareValues(col.fundExtract(a), col.fundExtract(b), sort.dir));
    });
    return grouped;
  }, [fundRows, sort]);

  if (loading) return <div className="stats-loading">Loading…</div>;
  if (!sacRows.length) return <div className="stats-empty">No Brinson data available.</div>;

  const total = portfolioTotal || sacRows.reduce((acc, s) => ({
    avg_portfolio_weight: acc.avg_portfolio_weight + (s.avg_portfolio_weight ?? 0),
    avg_benchmark_weight: acc.avg_benchmark_weight + (s.avg_benchmark_weight ?? 0),
    portfolio_return: null,
    benchmark_return: null,
    return_difference: null,
    allocation_effect: acc.allocation_effect + (s.allocation_effect ?? 0),
    selection_effect: acc.selection_effect + (s.selection_effect ?? 0),
    total_effect: acc.total_effect + (s.total_effect ?? 0),
  }), { avg_portfolio_weight: 0, avg_benchmark_weight: 0, allocation_effect: 0, selection_effect: 0, total_effect: 0 });

  const sortIndicator = (col) => {
    if (sort.key !== col.key) return null;
    return <span className="brinson-sort-indicator">{sort.dir === 'desc' ? ' ▼' : ' ▲'}</span>;
  };

  return (
    <div className="brinson-table-wrapper">
      <div className="brinson-table-controls">
        <button className="brinson-toggle-all" onClick={toggleAll}>
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>
      <table className="brinson-table">
        <thead>
          <tr>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                className={`${col.numeric ? 'num' : ''} brinson-sortable ${sort.key === col.key ? 'sorted' : ''}`}
                onClick={() => onSortClick(col)}
                title="Click to sort"
              >
                {col.label}{sortIndicator(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Portfolio total (never re-sorted; pinned at top) */}
          <tr className="brinson-total">
            <td>Portfolio Total</td>
            <td className="num">{formatWeight(total.avg_portfolio_weight)}</td>
            <td className="num">{formatWeight(total.avg_benchmark_weight)}</td>
            <td className="num">{formatWeight((total.avg_portfolio_weight ?? 0) - (total.avg_benchmark_weight ?? 0))}</td>
            <td className="num">{formatPct(total.portfolio_return)}</td>
            <td className="num">{formatPct(total.benchmark_return)}</td>
            <td className="num">{formatPct(total.return_difference)}</td>
            <td className={`num ${signClass(total.allocation_effect)}`}>{formatPct(total.allocation_effect)}</td>
            <td className={`num ${signClass(total.selection_effect)}`}>{formatPct(total.selection_effect)}</td>
            <td className={`num ${signClass(total.total_effect)}`}>{formatPct(total.total_effect)}</td>
          </tr>

          {sortedSacRows.map((sac) => {
            const isCollapsed = collapsedSacs.has(sac.sub_asset_class);
            return (
              <React.Fragment key={sac.sub_asset_class}>
                <tr className="brinson-sac" onClick={() => toggleSac(sac.sub_asset_class)}>
                  <td>
                    <span className="brinson-sac-toggle">{isCollapsed ? '▶' : '▼'}</span>
                    {sac.sub_asset_class}
                  </td>
                  <td className="num">{formatWeight(sac.avg_portfolio_weight)}</td>
                  <td className="num">{formatWeight(sac.avg_benchmark_weight)}</td>
                  <td className="num">{formatWeight((sac.avg_portfolio_weight ?? 0) - (sac.avg_benchmark_weight ?? 0))}</td>
                  <td className="num">{formatPct(sac.portfolio_return)}</td>
                  <td className="num">{formatPct(sac.benchmark_return)}</td>
                  <td className="num">{formatPct(sac.return_difference)}</td>
                  <td className="num" style={heatStyle(sac.allocation_effect, maxAbsEffect)}>{formatPct(sac.allocation_effect)}</td>
                  <td className="num" style={heatStyle(sac.selection_effect, maxAbsEffect)}>{formatPct(sac.selection_effect)}</td>
                  <td className="num" style={heatStyle(sac.total_effect, maxAbsEffect)}>{formatPct(sac.total_effect)}</td>
                </tr>
                {!isCollapsed && (sortedFundsBySac[sac.sub_asset_class] || []).map((fund, i) => (
                  <tr key={`${sac.sub_asset_class}-${i}`} className="brinson-fund">
                    <td className="brinson-fund-name">{fund.issue_name}</td>
                    <td className="num">{formatWeight(fund.avg_portfolio_weight)}</td>
                    <td className="num">{formatWeight(fund.avg_benchmark_weight)}</td>
                    <td className="num">{formatWeight(fund.avg_weight_difference)}</td>
                    <td className="num">{formatPct(fund.portfolio_return)}</td>
                    <td className="num">{formatPct(fund.benchmark_return)}</td>
                    <td className="num">{formatPct(fund.return_difference)}</td>
                    <td className="num">—</td>
                    <td className="num" style={heatStyle(fund.selection_effect, maxAbsEffect)}>{formatPct(fund.selection_effect)}</td>
                    <td className="num" style={heatStyle(fund.total_effect, maxAbsEffect)}>{formatPct(fund.total_effect)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BrinsonAttributionTable;
